<script setup>
import {computed, ref, watch} from 'vue'
import FormPageContentField from "@/common/form/FormPageContentField.vue"
import ContentUsageCount from "@/common/ContentUsageCount.vue"
import ItemStatusIcons from "@/common/ItemStatusIcons.vue"
import Button from '@/common/Button.vue'
import FormControlPlainText from "@/common/form-control/FormControlPlainText.vue"
import FormControlTypeSelect from "@/common/form-control-type/FormControlTypeSelect.vue"
import {usePageTemplatesContentAreasStore} from '@/stores/pageTemplatesContentAreas'
import {useItemDataStore} from '@/stores/itemData'

const props = defineProps({
    formId: String,
    templateId: Number
})

const pageTemplatesContentAreasStore = usePageTemplatesContentAreasStore()
const itemDataStore = useItemDataStore()

const button = ref(null)
const open = ref(false)

const buttonText = computed(() => open.value ? 'Hide' : 'Show')

const contentIds = ref([])
watch(
    () => props.templateId,
    async (newTemplateId) => {
        if (!newTemplateId) {
            contentIds.value = []
            return
        }
        contentIds.value = await pageTemplatesContentAreasStore.getPageTemplatesContentIds(newTemplateId)
    },
    {immediate: true}
)

const items = ref([])
watch(
    contentIds,
    async (newContentIds) => {
        if (!newContentIds || !newContentIds.length) {
            items.value = []
            return
        }
        items.value = await itemDataStore.preload({
            tableName: 'content',
            ids: newContentIds
        })
    },
    {immediate: true}
)

const template = ref(null)
watch(
    () => props.templateId,
    async (newTemplateId) => {
        if (!newTemplateId) {
            template.value = null
            return
        }
        template.value = await itemDataStore.get({
            tableName: 'templates_templates',
            id: newTemplateId
        })
    },
    {immediate: true}
)

const goToContent = (contentId) => {
    window.location = '/#/content/' + contentId
}

const toggleText = () => {
    open.value = button.value?.$el?.getAttribute('aria-expanded') === 'true'
}
</script>

<template>
    <div v-if="templateId" class="mb-3">
        <div class="d-flex align-items-center gap-2">
            <span class="m-0 form-text">Template:</span>

            <span class="flex-fill">{{ template?.title }}</span>

            <Button ref="button" @click="toggleText" :default-class="'btn-light'" class="btn-sm"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseExample" aria-expanded="false" aria-controls="collapseExample">
                {{ buttonText }} template content
            </Button>
        </div>

        <div class="collapse" id="collapseExample">
            <p class="ms-2 mt-3 mb-2 form-text small">
                <template v-if="items?.length">
                    The following content is embedded directly into the page template.
                    <br>
                    Changes will affect all pages of the same template.
                </template>
                <template v-else>
                    No content.
                </template>
            </p>

            <ul class="list-group pb-4">
                <li v-for="item in items" :key="item.id" class="list-group-item py-1 pe-2 d-flex align-items-center">
                    <div class="flex-fill">
                        <FormPageContentField :content-id="item.id"/>
                    </div>

                    <ItemStatusIcons :id="item.id" :table-name="'content'"/>

                    <Button class="btn-sm" @click="goToContent(item.id)">
                        <i class="bi-pencil-square"/>
                    </Button>
                </li>
            </ul>
        </div>
    </div>
</template>